import { useState } from 'react';
import { CheckCircle2, Download, FileText, Loader2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SectionHeader } from '@/components/ui/section-header';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorState } from '@/components/ui/error-state';
import { Skeleton } from '@/components/ui/skeleton';
import { open_file_in_new_tab } from '@/components/auth-image';
import { form_error_message } from '@/lib/forms';
import { date_label } from '@/lib/datetime';
import { use_accept_agreement, use_vendor_agreements } from '@/features/agreements/hooks';
import type { VendorAgreement } from '@/features/agreements/types';

function AgreementCard({ agreement: a }: { agreement: VendorAgreement }) {
  const accept_m = use_accept_agreement();
  const [confirming, set_confirming] = useState(false);
  const [opening, set_opening] = useState(false);
  const [error, set_error] = useState<string | null>(null);

  async function handle_download() {
    set_error(null);
    set_opening(true);
    try {
      await open_file_in_new_tab(a.file_path);
    } catch {
      set_error('Could not open the agreement. Please try again.');
    } finally {
      set_opening(false);
    }
  }

  async function handle_accept() {
    set_error(null);
    try {
      await accept_m.mutateAsync(a.id);
      set_confirming(false);
    } catch (err) {
      set_error(form_error_message(err, 'Could not accept the agreement'));
    }
  }

  return (
    <Card className="flex flex-col gap-3 p-4" data-testid={`agreement-${a.module_type}`}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <FileText className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
          <div className="min-w-0">
            <p className="flex flex-wrap items-center gap-2 font-heading font-bold">
              {a.module_label} agreement · v{a.version}
              {!a.in_effect && (
                <span
                  className="inline-flex items-center rounded-full bg-info px-2.5 py-0.5 text-xs font-semibold text-info-foreground"
                  data-testid={`agreement-upcoming-${a.module_type}`}
                >
                  Applies from {date_label(a.effective_from)}
                </span>
              )}
            </p>
            <p className="text-xs text-muted-foreground">
              Published {date_label(a.published_at)}
              {a.in_effect ? ` · Effective ${date_label(a.effective_from)}` : ''}
            </p>
            {a.change_summary && (
              <p className="mt-1 whitespace-pre-wrap text-sm" data-testid={`agreement-summary-${a.module_type}`}>
                {a.change_summary}
              </p>
            )}
          </div>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="shrink-0"
          disabled={opening}
          onClick={handle_download}
          data-testid={`agreement-download-${a.module_type}`}
        >
          {opening ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          Download
        </Button>
      </div>

      {a.accepted_at ? (
        <p
          className="flex items-center gap-1.5 text-sm font-semibold text-success"
          data-testid={`agreement-accepted-${a.module_type}`}
        >
          <CheckCircle2 className="h-4 w-4" /> Accepted on {date_label(a.accepted_at)}
        </p>
      ) : confirming ? (
        <div
          className="flex flex-col gap-2 rounded-md border-2 border-primary p-3 sm:flex-row sm:items-center sm:justify-between"
          data-testid={`agreement-confirm-${a.module_type}`}
        >
          <p className="text-sm">
            Accept the {a.module_label} agreement (v{a.version}) on behalf of your business?
          </p>
          <div className="flex shrink-0 gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={accept_m.isPending}
              onClick={() => set_confirming(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              variant="gradient"
              disabled={accept_m.isPending}
              onClick={handle_accept}
              data-testid={`agreement-confirm-accept-${a.module_type}`}
            >
              {accept_m.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Yes, I accept
            </Button>
          </div>
        </div>
      ) : (
        <Button
          type="button"
          size="sm"
          variant="gradient"
          className="self-start"
          onClick={() => set_confirming(true)}
          data-testid={`agreement-accept-${a.module_type}`}
        >
          I accept
        </Button>
      )}

      {error && (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )}
    </Card>
  );
}

export function VendorAgreementsPage() {
  const q = use_vendor_agreements();

  return (
    <div className="flex flex-col gap-6">
      <SectionHeader
        title="Agreements"
        description="The current agreement for each of your services. Review updates and accept them here."
      />
      {q.isLoading ? (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-28 w-full" />
        </div>
      ) : q.isError ? (
        <ErrorState
          description="Could not load your agreements."
          action={
            <Button size="sm" variant="outline" onClick={() => q.refetch()}>
              Try again
            </Button>
          }
        />
      ) : !q.data || q.data.agreements.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No agreements yet"
          description="Agreements for your services will appear here once they are published."
        />
      ) : (
        <div className="flex flex-col gap-3" data-testid="agreements-list">
          {q.data.agreements.map((a) => (
            <AgreementCard key={a.id} agreement={a} />
          ))}
        </div>
      )}
    </div>
  );
}
